import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { getGroupAllPayments } from 'services/easyPay'
import {
    BUCKET_ORDER,
    Payable,
    PayableBucket,
    PreselectMode,
    homeSummary,
    payableKey,
    selectedTotals,
    summarise,
    toPayables,
} from 'services/easyPay/payables'

/**
 * The single source of "what does this user owe".
 *
 * Shares the react-query key `['get-all-payment']` with the AccessPay banner
 * on /funds and with the pay screen, so mounting the home page costs no extra
 * request — the landing path already fetches this.
 */
export const PAYABLES_QUERY_KEY = ['get-all-payment']

interface Options {
    /** which buckets arrive ticked; see PreselectMode */
    preselect?: PreselectMode
    /** limit to one box, for the per-fund entry point */
    boxId?: number
}

export default function usePayables({
    preselect = 'overdue',
    boxId,
}: Options = {}) {
    const queryClient = useQueryClient()
    const { data, isLoading, isError, refetch } = useQuery(
        PAYABLES_QUERY_KEY,
        getGroupAllPayments,
        {
            // The global config is refetchOnWindowFocus with no stale time, so
            // every tab focus refired this. Home mounts it on every app open.
            staleTime: 60_000,
        }
    )

    const scoped = useMemo(
        () => (boxId ? data?.filter((i) => i.box_id === boxId) : data),
        [data, boxId]
    )

    const [payables, setPayables] = useState<Payable[]>([])

    // `reconcile` needs the live selection without taking `payables` as a
    // dependency, which would rebuild the callback on every tick.
    const payablesRef = useRef<Payable[]>([])
    payablesRef.current = payables

    useEffect(() => {
        setPayables(toPayables(scoped, preselect))
    }, [scoped, preselect])

    const toggle = useCallback((key: string) => {
        setPayables((prev) =>
            prev.map((p) =>
                // Compare on the composite key. Matching on `id` alone made a
                // Poolship and an Installment sharing a pk toggle together.
                p.key === key && p.bucket !== 'record'
                    ? { ...p, isSelected: !p.isSelected }
                    : p
            )
        )
    }, [])

    const toggleBucket = useCallback((bucket: PayableBucket) => {
        setPayables((prev) => {
            const inBucket = prev.filter((p) => p.bucket === bucket)
            const turnOn = inBucket.some((p) => !p.isSelected)
            return prev.map((p) =>
                p.bucket === bucket ? { ...p, isSelected: turnOn } : p
            )
        })
    }, [])

    const toggleAll = useCallback(() => {
        setPayables((prev) => {
            const selectable = prev.filter((p) => p.bucket !== 'record')
            const turnOn = selectable.some((p) => !p.isSelected)
            return prev.map((p) =>
                p.bucket === 'record' ? p : { ...p, isSelected: turnOn }
            )
        })
    }, [])

    /**
     * Re-read the server list and fold it onto the current selection.
     *
     * `PaymentItemSerializer.validate_itemId` silently rewrites both the item
     * id and the amount to the loan's next unpaid installment when the one
     * submitted was already paid — so a user can be charged an amount that
     * differs from the receipt they approved. Called on the pay CTA: if
     * anything moved, the basket is rebuilt and the user confirms again.
     *
     * Returns true when the basket changed under them.
     */
    const reconcile = useCallback(async () => {
        const { data: fresh } = await refetch()
        if (!fresh) return false

        const freshScoped = boxId
            ? fresh.filter((i) => i.box_id === boxId)
            : fresh
        const freshByKey = new Map(freshScoped.map((i) => [payableKey(i), i]))

        // Compare against the CURRENT state value, not inside the setState
        // updater: updaters run during render, so a flag set in there is still
        // false by the time this function returns. Getting that wrong would
        // silently skip the confirmation step and charge the user anyway.
        const current = payablesRef.current
        const changed = current.some((p) => {
            if (!p.isSelected) return false
            const now = freshByKey.get(p.key)
            return !now || now.amount !== p.amount
        })

        const previouslySelected = new Set(
            current.filter((p) => p.isSelected).map((p) => p.key)
        )
        setPayables(
            toPayables(freshScoped, preselect).map((p) => ({
                ...p,
                // Preserve what the user ticked; anything newly appearing stays
                // unticked so nothing is ever added to a basket behind them.
                isSelected:
                    p.bucket !== 'record' && previouslySelected.has(p.key),
            }))
        )
        return changed
    }, [refetch, boxId, preselect])

    const invalidate = useCallback(
        () => queryClient.invalidateQueries(PAYABLES_QUERY_KEY),
        [queryClient]
    )

    const buckets = useMemo(
        () =>
            BUCKET_ORDER.map((b) => summarise(payables, b)).filter(
                (s) => s.count > 0
            ),
        [payables]
    )

    const totals = useMemo(() => selectedTotals(payables), [payables])
    const summary = useMemo(() => homeSummary(payables), [payables])

    return {
        payables,
        buckets,
        totals,
        summary,
        isLoading,
        isError,
        toggle,
        toggleBucket,
        toggleAll,
        reconcile,
        invalidate,
        refetch,
    }
}
